import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setToken, setRemember, bootstrapNativeToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
    } catch (err) {
      // Stale or invalid token — clear it so future logins start fresh
      if (err?.response?.status === 401) {
        setToken(null);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from OAuth callback, skip /me check; AuthCallback handles it
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // On native (Android), rehydrate the token from Capacitor Preferences
    // BEFORE calling /me — otherwise the interceptor sends no Bearer header
    // and every cold-start looks logged-out.
    (async () => {
      await bootstrapNativeToken();
      await checkAuth();
    })();
  }, [checkAuth]);

  const login = async (email, password, remember = true) => {
    setRemember(remember);
    const r = await api.post("/auth/login", { email, password });
    setToken(r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  const register = async (email, password, name, remember = true) => {
    setRemember(remember);
    const r = await api.post("/auth/register", { email, password, name });
    setToken(r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  const logout = async () => {
    // Drop this device's push token server-side so a logged-out shared device
    // doesn't keep receiving pings for the previous owner. Best-effort — the
    // token also gets pruned the next time FCM rejects it.
    try {
      const { unregisterPushNotifications } = await import("./push");
      await unregisterPushNotifications();
    } catch (_err) { /* ignore */ }
    try { await api.post("/auth/logout"); } catch (_err) { /* ignore */ }
    setToken(null);
    setUser(null);
    try { const { clearOfflineCache } = await import("./offline"); await clearOfflineCache(); } catch (_err) { /* ignore */ }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh: checkAuth, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
